import React from 'react';
import { Button, Progress, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  StarFilled,
  TrophyOutlined,
} from '@ant-design/icons';

import { DevProfile } from '../types';

type Props = {
  profile: DevProfile;
  /** The coin wallet — the currency that replaced XP/levels/skill points. */
  coins?: number;
  dailyGrant?: number;
  planName?: string;
  unlockedCount: number;
  totalCount: number;
  /** Visible recommendations for this trainee; 0 when the feature is off. */
  recommendedCount?: number;
  onBack?: () => void;
};

const ProgressHeader: React.FC<Props> = ({
  profile,
  coins,
  dailyGrant,
  planName,
  unlockedCount,
  totalCount,
  recommendedCount = 0,
  onBack,
}) => {
  const xpPct = Math.round(
    (profile.xp_into_level / profile.xp_to_next_level) * 100
  );

  return (
    <div className="devplan__topbar">
      <div className="devplan__brand">
        {onBack && (
          <Tooltip title="All development plans">
            <Button
              size="small"
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="devplan__back"
            />
          </Tooltip>
        )}
        <div className="devplan__brand-mark">
          <TrophyOutlined />
        </div>
        <div className="devplan__brand-text">
          <div className="devplan__brand-title">{planName || 'Development Plan'}</div>
          <div className="devplan__brand-sub">
            Your progression map toward the macro-trading bar
          </div>
        </div>
      </div>

      <div className="devplan__stats">
        {coins != null ? (
          <Tooltip
            title={`Your coins. Plays cost coins, passes earn them, and ${
              dailyGrant ?? 15
            } arrive free every day.`}
          >
            <div className="devplan__chip devplan__chip--coins">
              <strong>{coins.toLocaleString()}</strong> coins
            </div>
          </Tooltip>
        ) : (
          <>
            <div className="devplan__stat">
              <span className="devplan__stat-label">Level</span>
              <span className="devplan__stat-level">{profile.level}</span>
            </div>
            <div className="devplan__stat devplan__stat--xp">
              <div className="devplan__stat-label">
                XP&nbsp;
                <span className="devplan__muted">
                  {profile.xp_into_level.toLocaleString()} /{' '}
                  {profile.xp_to_next_level.toLocaleString()}
                </span>
              </div>
              <Progress
                percent={xpPct}
                showInfo={false}
                strokeColor="#0f5fd6"
                trailColor="rgba(15,95,214,0.12)"
                size="small"
              />
            </div>
            <Tooltip title="Skill points available to invest">
              <div className="devplan__chip devplan__chip--sp">
                <strong>{profile.available_skill_points}</strong> SP
              </div>
            </Tooltip>
          </>
        )}

        <Tooltip title="Skills unlocked across all tracks">
          <div className="devplan__chip">
            {unlockedCount}
            <span className="devplan__muted"> / {totalCount}</span> skills
          </div>
        </Tooltip>

        {recommendedCount > 0 && (
          <Tooltip title="Skills recommended for practice. Unlock and play costs still apply.">
            <div className="devplan__chip devplan__chip--reco">
              <StarFilled /> <strong>{recommendedCount}</strong> recommended
            </div>
          </Tooltip>
        )}

        {/* Two dev/test controls used to close out this bar and are now hidden:
            the "Locked progression / Open play" switch — progression mode is no
            longer user-facing, `?devPass=1` selects open play (see
            useDevPassAccess) — and a "Reset" button that re-fetched the tree
            from the server. The `.devplan__lockmode` CSS is kept for a
            straightforward revert. */}
      </div>
    </div>
  );
};

export default ProgressHeader;
